import React from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * 환자 한 명을 보는 일곱 화면의 공용 탭 줄. 규격: docs/design.md 6.2
 *
 * 일곱 화면이 저마다 다른 라우트라 탭은 상태가 아니라 링크다. 주소를 그대로 공유할 수
 * 있다. 화면마다 이 컴포넌트를 자기 헤더 아래에 두고 active 로 자기를 가리킨다.
 *
 * ⚠️ 탭 이동은 **기록을 쌓지 않고 바꿔 끼운다(replace)**. 2026-09-17 결정: 대상자 화면에서
 *    브라우저 뒤로가기를 누르면 바로 대시보드로 돌아가야 한다. 탭마다 기록이 쌓이면
 *    개요→측정기록→케어플랜을 본 담당자는 뒤로가기를 세 번 눌러야 목록으로 나간다.
 *    탭은 한 대상자를 보는 방식이지 이동한 곳이 아니다 — 목록으로 나가는 길은
 *    브레드크럼의 '대시보드' 하나다.
 *
 * 스마트링 탭은 착용자에게만 보인다. 미착용자에게 빈 화면으로 가는 탭을 보이면
 * "뭔가 있는데 안 나온다"로 읽힌다.
 *
 * ⚠️ 조건부 항목(needsRing)은 이 배열의 맨 끝에 둔다. 새 조건부 항목이 생겨도 끝이다.
 *
 * 가운데에 두면 그 뒤 항목이 전부 밀린다. 스마트링이 3번이던 동안, 뒤 세 화면
 * (케어플랜·설문·보고서)이 hasSmartRing 을 모르는 채 false 로 그려서 링 착용자가 그 셋
 * 중 하나로 옮기면 뒤쪽 탭이 91px 씩 왼쪽으로 이동했다 — 개요에서 케어플랜(x≈298)을
 * 누르면 도착한 화면의 같은 좌표에 설문이 앉아 있었다. 케어플랜은 첫 프레임에
 * "1~2분 정도 걸릴 수 있습니다"를 띄워 재클릭을 부르므로, 두 번째 클릭이 설문으로 갔다.
 *
 * 앞이 고정이고 뒤가 변하는 줄은 익힐 수 있지만 가운데가 변하는 줄은 못 익힌다.
 * flex order 로 시각 순서만 옮기는 방법은 초점 순서가 어긋나 포인터 위험을 키보드
 * 위험으로 바꿀 뿐이고, 자리를 붙드는 빈 요소는 보이지 않는데 지우면 줄이 깨진다.
 */
const TABS = [
    { key: 'overview', label: '개요', to: (id) => `/DashboardDetail/${id}` },
    { key: 'records', label: '측정기록', to: (id) => `/DashboardDetail/${id}/records` },
    // 노쇠지수(스마트 프레일 지수·AI 브리핑·권장 운동). 2026-10-16 사용자: 개요에 넣지 말고 탭으로.
    { key: 'frail', label: '노쇠지수', to: (id) => `/DashboardDetail/${id}/frail` },
    { key: 'careplan', label: '케어플랜', to: (id) => `/careplan/${id}` },
    { key: 'survey', label: '설문', to: (id) => `/survey/${id}` },
    { key: 'report', label: '보고서', to: (id) => `/health-report/${id}` },
    { key: 'ring', label: '스마트링', to: (id) => `/VitalsDetail/${id}`, needsRing: true },
];

export default function PatientTabs({ appUserId, active, hasSmartRing = false }) {
    // 라우터 state 중 dashboardState 하나만 골라 다음 화면으로 넘긴다.
    //
    // 대시보드에서 대상자를 열면 state.dashboardState(쪽 번호·검색어·필터)가 함께 실리고,
    // DashboardDetail 의 handleBackToDashboard 가 그것으로 목록을 되살린다. 탭이 그것을
    // 안 실으면 한 번만 눌러도 값이 null 이 되어, 브레드크럼의 "대시보드"가 복원 없이
    // window.history.back() 으로 떨어진다 — 담당자가 3쪽 위험 필터로 되돌아오지 못한다.
    //
    // ⚠️ state 를 통째로 넘기지 말 것. 같은 state 에 다른 키가 산다 — VitalsDetail 은
    //    location.state.patientName 을 읽어 제목에 쓴다. 두 값은 낡았을 때의 성질이 다르다.
    //    낡은 dashboardState 는 목록이 사용자가 떠난 쪽·필터로 돌아갈 뿐이고, 그것은 지금
    //    뒤로가기가 하는 일과 같다. 낡은 patientName 은 **다른 대상자의 화면에 남의 이름을
    //    띄운다.** 곱게 낡지 않는 값은 함께 태우지 않는다. hasSmartRing 도 같은 이유로 제외다.
    const location = useLocation();
    const forwardedState = location.state?.dashboardState
        ? { dashboardState: location.state.dashboardState }
        : undefined;

    return (
        <nav className="sds-tabs" aria-label="대상자 화면">
            {TABS.filter((tab) => !tab.needsRing || hasSmartRing).map((tab) => (
                <Link
                    key={tab.key}
                    to={tab.to(appUserId)}
                    state={forwardedState}
                    replace
                    className={`sds-tabs__item${active === tab.key ? ' is-active' : ''}`}
                    aria-current={active === tab.key ? 'page' : undefined}
                >
                    {tab.label}
                </Link>
            ))}
        </nav>
    );
}
